// Select: dropdown nativo, melhor para listas pequenas com opções fixas.
// Para listas grandes com busca em tempo real (ex: municípios), usar SearchableSelect.

import React, { forwardRef } from 'react';
import styled from 'styled-components';
import { FaTimes } from 'react-icons/fa';
import { colors, breakpoints } from '../../../assets/styles/GlobalStyles';

// Mantém posição relativa para o botão limpar
const SelectContainer = styled.div`
  width: 100%;
  margin-bottom: 1.2rem;
  position: relative;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: ${colors.darkBlue};
  font-weight: 600;
  font-size: 0.9rem;

  span {
    color: #ff4444;
  }
`;

const StyledSelect = styled.select`
  width: 100%;
  padding: 0.9rem;
  padding-right: ${props => props.$hasClear ? '2.5rem' : '0.9rem'}; /* Espaço para botão limpar */
  border: 2px solid ${props => props.$hasError ? '#ff4444' : '#e8ecef'};
  border-radius: 8px;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  box-sizing: border-box;
  background: ${props => props.disabled ? '#f5f5f5' : colors.backgroundLight};
  color: #333;
  appearance: none; /* Remove seta padrão do navegador */
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};

  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? '#ff4444' : colors.lightBlue};
    background: white;
  }

  // 16px evita o zoom automático do Safari/iOS ao focar o campo.
  @media (max-width: ${breakpoints.mobile}) {
    padding: 0.8rem;
    padding-right: ${props => props.$hasClear ? '2.5rem' : '0.8rem'};
    font-size: 16px;
  }
`;

// Aparece apenas quando showClear=true e há valor selecionado
const ClearButton = styled.button`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;

  &:hover {
    color: #ff4444;
  }
`;

const ErrorMessage = styled.small`
  color: #ff4444;
  font-size: 0.8rem;
  margin-top: 0.3rem;
  display: block;
`;

/**
 * @param {Function} [props.onClear] - Callback para limpar a seleção (usado com showClear)
 * @param {boolean} [props.showClear] - Se true, mostra botão para limpar quando há valor selecionado
 */
export const Select = forwardRef(({
  label,
  error,
  children,
  required,
  id,
  onClear,
  showClear,
  value,
  ...props
}, ref) => {
  const selectId = id || `select-${Math.random().toString(36).substr(2, 9)}`;
  const hasClear = showClear && value && !props.disabled;

  return (
    <SelectContainer>
      {label && (
        <Label htmlFor={selectId}>
          {label} {required && <span>*</span>}
        </Label>
      )}

      <div style={{ position: 'relative' }}>
        <StyledSelect
          ref={ref}
          id={selectId}
          value={value}
          $hasError={!!error}
          $hasClear={hasClear}
          {...props}
        >
          {children}
        </StyledSelect>

        {hasClear && (
          <ClearButton
            type="button"
            onClick={onClear}
            aria-label="Limpar seleção"
          >
            <FaTimes size={14} />
          </ClearButton>
        )}
      </div>

      {error && <ErrorMessage>{error}</ErrorMessage>}
    </SelectContainer>
  );
});
